import { RecordAny } from '@dealstack/shared/lib/common/common';
import {
	DATE_FORMATS,
	formatDateToUI,
	formatStringDateToUI,
} from '@dealstack/shared/lib/format/dates/date';
import { HYPHEN_MINUS, LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { Text } from '@mantine/core';
import { format, isDate } from 'date-fns';
import {
	DetailsDiv,
	InfoDiv,
	TotalDetailsDiv,
	UniqueDiv,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { DateInput } from '@mantine/dates';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { formatNumber } from '@dealstack/shared/lib/format/number';

type Props<T extends RecordAny> = ExtendedColumnDef<T> & {
	dateFormat?: DATE_FORMATS;
	showFooter?: boolean;
	footerValue?: string | number;
	text?: string;
	headerText?: string;
	enableSorting?: boolean;
};

export const createDateColumn = <T extends RecordAny>({
	dateFormat = DATE_FORMATS.CALENDAR_DATE,
	filterValueType = 'date',
	filterVariant = 'date-range',
	showFooter = false,
	footerValue,
	text,
	headerText,
	enableSorting = true,
	sortingFn = 'datetime',
	...columnDef
}: Props<T>): ExtendedColumnDef<T> => {
	return {
		filterValueType,
		filterVariant,
		header: headerText ? createSortableTextHeader(headerText, enableSorting) : columnDef.header,
		columnLabel: columnDef.columnLabel ?? headerText ?? '',
		enableSorting,
		sortingFn,
		cell: ({ cell }) => {
			const date = cell.getValue<Date | string>();

			if (typeof date === 'string') {
				return (
					<Text size="B6Medium">
						{formatStringDateToUI(date, dateFormat) || LONG_DASH}
					</Text>
				);
			}

			if (!date || !isDate(date)) {
				return LONG_DASH;
			}

			return <Text size="B6Medium">{formatDateToUI(date, dateFormat)}</Text>;
		},
		filter: ({ column }) => {
			const current = column.getFilterValue();
			if (filterVariant === 'date-range') {
				const [from, to] = (Array.isArray(current) ? current : [null, null]) as [
					Date | null,
					Date | null,
				];
				return (
					<div style={{ display: 'flex', gap: 8 }}>
						<DateInput
							value={from ?? null}
							onChange={(val) => column.setFilterValue([val ?? null, to] as unknown)}
							clearable
							placeholder="Min"
							popoverProps={{
								withinPortal: true,
								zIndex: Z_INDEX.MENU,
							}}
							styles={{
								input: {
									borderColour: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									'&:hover': {
										borderColor: defaultTheme.v3.colors.dark[0],
									},
									'&:focus': {
										borderColor: defaultTheme.v3.colors.dark[0],
									},
									'&:focus-within': {
										borderColor: defaultTheme.v3.colors.dark[0],
									},
								},
							}}
						/>
						<DateInput
							value={to ?? null}
							onChange={(val) =>
								column.setFilterValue([from ?? null, val ?? null] as unknown)
							}
							clearable
							placeholder="Max"
							popoverProps={{
								withinPortal: true,
								zIndex: Z_INDEX.MENU,
							}}
							styles={{
								input: {
									borderColour: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									'&:hover': {
										borderColor: defaultTheme.v3.colors.dark[0],
									},
									'&:focus': {
										borderColor: defaultTheme.v3.colors.dark[0],
									},
									'&:focus-within': {
										borderColor: defaultTheme.v3.colors.dark[0],
									},
								},
							}}
						/>
					</div>
				);
			}

			return (
				<DateInput
					placeholder={columnDef.filterText}
					value={(current as Date | null) ?? null}
					onChange={(val) => column.setFilterValue(val ?? null)}
					clearable
					popoverProps={{
						withinPortal: true,
						zIndex: Z_INDEX.MENU,
					}}
					styles={{
						input: {
							borderColour: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
							'&:hover': {
								borderColor: defaultTheme.v3.colors.dark[0],
							},
							'&:focus': {
								borderColor: defaultTheme.v3.colors.dark[0],
							},
							'&:focus-within': {
								borderColor: defaultTheme.v3.colors.dark[0],
							},
						},
					}}
				/>
			);
		},
		footer: showFooter
			? () => (
					<DetailsDiv>
						<UniqueDiv>{text}</UniqueDiv>
						<InfoDiv>{footerValue}</InfoDiv>
					</DetailsDiv>
				)
			: undefined,
		...columnDef,
	};
};

export const createPrismaDateColumn = <T extends RecordAny>({
	dateFormat = DATE_FORMATS.CALENDAR_DATE,
	filterValueType = 'date',
	filterVariant = 'date',
	headerText,
	text,
	showFooter = false,
	footerValue,
	...columnDef
}: Props<T>): ExtendedColumnDef<T> => {
	return {
		filterValueType,
		filterVariant,
		header: headerText
			? createSortableTextHeader(headerText, columnDef.enableSorting)
			: columnDef.header,
		columnLabel: columnDef.columnLabel ?? headerText ?? '',
		cell: ({ cell }) => {
			const rawValue = cell.getValue();
			let date: Date | null = null;

			if (rawValue instanceof Date) {
				date = rawValue;
			} else if (typeof rawValue === 'string') {
				date = new Date(rawValue);
			}

			if (!date || isNaN(date.getTime())) {
				return '—';
			}

			return <Text size="B6Medium">{format(date, dateFormat)}</Text>;
		},
		footer: showFooter
			? () => {
					let formattedFooterValue = '';
					if (typeof footerValue === 'number') {
						formattedFooterValue = formatNumber({
							value: footerValue,
						});
					} else {
						formattedFooterValue = HYPHEN_MINUS;
					}

					return (
						<TotalDetailsDiv>
							<UniqueDiv>{text}</UniqueDiv>
							<InfoDiv>{formattedFooterValue}</InfoDiv>
						</TotalDetailsDiv>
					);
				}
			: undefined,
		filter:
			columnDef.enableColumnFilter === false
				? () => <div />
				: ({ column }) => {
						const current = column.getFilterValue();
						if (filterVariant === 'date-range') {
							const [from, to] = (
								Array.isArray(current) ? current : [null, null]
							) as [Date | null, Date | null];
							return (
								<div style={{ display: 'flex', gap: 8 }}>
									<DateInput
										value={from ?? null}
										onChange={(val) =>
											column.setFilterValue([val ?? null, to] as unknown)
										}
										clearable
										placeholder="Min"
										popoverProps={{
											withinPortal: true,
											zIndex: Z_INDEX.MENU,
										}}
										styles={{
											input: {
												borderColour: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
												'&:hover': {
													borderColor: defaultTheme.v3.colors.dark[0],
												},
												'&:focus': {
													borderColor: defaultTheme.v3.colors.dark[0],
												},
												'&:focus-within': {
													borderColor: defaultTheme.v3.colors.dark[0],
												},
											},
										}}
									/>
									<DateInput
										value={to ?? null}
										onChange={(val) =>
											column.setFilterValue([
												from ?? null,
												val ?? null,
											] as unknown)
										}
										clearable
										placeholder="Max"
										popoverProps={{
											withinPortal: true,
											zIndex: Z_INDEX.MENU,
										}}
										styles={{
											input: {
												borderColour: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
												'&:hover': {
													borderColor: defaultTheme.v3.colors.dark[0],
												},
												'&:focus': {
													borderColor: defaultTheme.v3.colors.dark[0],
												},
												'&:focus-within': {
													borderColor: defaultTheme.v3.colors.dark[0],
												},
											},
										}}
									/>
								</div>
							);
						}

						return (
							<DateInput
								value={(current as Date | null) ?? null}
								onChange={(val) => column.setFilterValue(val ?? null)}
								clearable
								popoverProps={{
									withinPortal: true,
									zIndex: Z_INDEX.MENU,
								}}
								styles={{
									input: {
										border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									},
								}}
							/>
						);
					},
		...columnDef,
	};
};

export const createEventDate = <T extends RecordAny>({
	dateFormat = DATE_FORMATS.CALENDAR_DATE,
	filterValueType = 'date',
	filterVariant = 'date',
	headerText,
	...columnDef
}: Props<T>): ExtendedColumnDef<T> => {
	return {
		filterValueType,
		filterVariant,
		header: headerText ? () => <Text fw={700}>{headerText}</Text> : columnDef.header,
		columnLabel: columnDef.columnLabel ?? headerText ?? '',
		cell: ({ cell }) => {
			const date = cell.getValue<Date>();
			if (!date || !isDate(date)) {
				return LONG_DASH;
			}

			return <Text size="B6Medium">{formatDateToUI(date, dateFormat)}</Text>;
		},

		...columnDef,
	};
};
